/**
 * TopicGroupItem — compact email row with expandable body and inline actions.
 *
 * Renders inside a TopicGroup (CollapsiblePanel). Clicking the subject/from
 * area toggles the email body inline.
 */
import React, { useState } from 'react';
import { Flag, Paperclip } from 'lucide-react';
import { cn, ICON_PX } from '../../../ui';
import { TimeAgo } from '../shared/TimeAgo';
import { UrgencyIndicator } from '../shared/UrgencyIndicator';
import { TopicGroupItemActions } from './TopicGroupItemActions';
import { useInbox } from '../InboxContext';

function TopicGroupItem({ email }) {
  const { actions } = useInbox();
  const [expanded, setExpanded] = useState(false);

  const handleClick = () => {
    if (!email.isRead) actions.markRead(email.id);
    setExpanded((v) => !v);
  };

  return (
    <div
      className={cn(
        'rounded-md border bg-nn-white px-3 py-2 transition-colors',
        expanded ? 'border-aqua/30 shadow-sm' : 'border-nn-border hover:bg-surface-warm/50',
      )}
    >
      {/* Header row — clickable */}
      <button
        type="button"
        onClick={handleClick}
        className="flex items-start gap-2 w-full text-left min-w-0"
      >
        {/* Unread dot */}
        <span className="shrink-0 mt-1.5" style={{ width: 6, height: 6, borderRadius: '50%', background: email.isRead ? 'transparent' : 'var(--aqua)' }} />

        {/* Star */}
        <span className="shrink-0 mt-0.5">
          <Flag
            size={ICON_PX.sm}
            style={{ color: email.isStarred ? 'var(--red-critical)' : 'var(--surface)' }}
            fill={email.isStarred ? 'var(--red-critical)' : 'none'}
          />
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={cn('text-[12px] leading-normal truncate min-w-0', !email.isRead && 'font-semibold text-text-dark')}>
              {email.from.name}
            </span>
            <UrgencyIndicator urgency={email.urgency} />
            {email.attachments?.length > 0 && (
              <Paperclip size={ICON_PX.sm} className="text-text-muted shrink-0" />
            )}
            <TimeAgo date={email.receivedAt} className="ml-auto !text-body-sm" />
          </div>
          <div className={cn('text-[12px] leading-normal truncate min-w-0 mt-0.5', !email.isRead ? 'text-text-dark' : 'text-text-dark-secondary')}>
            {email.subject}
          </div>
          {!expanded && (
            <div className="text-[12px] leading-normal text-text-muted truncate min-w-0 mt-0.5">
              {email.preview}
            </div>
          )}
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="mt-2 ml-8 border-t border-nn-border pt-2">
          {/* Email body */}
          <div
            className="text-body-sm text-text-dark leading-relaxed break-words"
            dangerouslySetInnerHTML={{ __html: email.bodyHtml || email.bodyText }}
          />

          {/* Per-email actions: Reply, Archive/Delete, Flag */}
          <TopicGroupItemActions email={email} scope="email" />

          {/* Attachments */}
          {email.attachments?.length > 0 && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {email.attachments.map((att) => (
                <span
                  key={att.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-surface text-body-sm text-text-dark-secondary"
                >
                  <Paperclip size={ICON_PX.xs} />
                  {att.filename}
                </span>
              ))}
            </div>
          )}

        </div>
      )}
    </div>
  );
}

export { TopicGroupItem };
